
import {
  defineComponent,
  onBeforeMount,
  PropType,
  ref,
  computed,
  watch
} from "vue";
import { useStore } from "vuex";
import { Alternative, QuestionType } from "./helpers";
import { formattedDuration } from "../../components/mixins";
import { wrapLoading } from "../../utils/wrap_loading";
import { useRoute, useRouter } from "vue-router";
import TUIViewer from "../../components/application/TUIViewer.vue";
import { updateQuery } from "../../utils/routing_utils";
import { dispatchError } from "../../utils/error_handling";
import Dot from "../../components/application/Dot.vue";
import { Exercise } from "../../../../domain/entities/exercise/exercise";

export default defineComponent({
  name: "UserResponses",
  components: { Dot, TUIViewer },
  props: {
    questionnaireId: {
      type: Number as PropType<number>,
      required: true
    }
  },
  setup(props) {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const currentIndex = ref(0);
    const visitedQuestions = ref<Set<number>>(new Set());

    const codeShowAnswers = computed(() =>
      Number(route.query.codeShowAnswers || 0)
    );

    const responseQuestion = computed<Exercise>(() => {
      return store.getters["question_module/responseQuestion"];
    });

    const currentQuestion = computed(
      () => responseQuestion.value.questions?.[currentIndex.value] || null
    );

    function isCorrectQuestion(index: number) {
      return responseQuestion.value.questions?.[index].isCorrect === true;
    }

    function isIncorrectQuestion(index: number) {
      return responseQuestion.value.questions?.[index].isCorrect === false;
    }

    function isCorrectionQuestion(index: number) {
      const question = responseQuestion.value.questions?.[index];
      return (
        Number(question?.questionType) === QuestionType.Descriptive &&
        question?.isCorrect == null
      );
    }

    const isUnansweredQuestion = (index: number) => {
      const question = responseQuestion.value.questions?.[index];
      return (
        Number(question?.questionType) !== QuestionType.Descriptive &&
        question?.isCorrect == null
      );
    };

    function isAlternativeSelected(alternative: Alternative) {
      return currentQuestion.value.answeredAlternativeId === alternative.id;
    }

    function isCorrectAlternative(alternative: Alternative) {
      const question = currentQuestion.value;
      if (!question) {
        return false;
      }

      return (
        (isAlternativeSelected(alternative) && question.isCorrect) ||
        (!isAlternativeSelected(alternative) &&
          codeShowAnswers.value >= 2 &&
          !question.isCorrect &&
          alternative.isCorrect)
      );
    }

    const isIncorrectAlternative = (alternative: Alternative) => {
      const question = currentQuestion.value;
      return (
        question && isAlternativeSelected(alternative) && !question.isCorrect
      );
    };

    const feedbackMessage = computed(() => {
      const question = currentQuestion.value;
      if (!question) return "";
      if (question.isCorrect == null) {
        return Number(question.questionType) === QuestionType.Descriptive
          ? "A resposta está pendente de correção."
          : "Pergunta não respondida.";
      }
      return question.isCorrect
        ? "Parabéns, você acertou!!"
        : "A alternativa está incorreta...";
    });

    const timeElapsed = computed(() => {
      const createdAt = responseQuestion.value.createdAt;
      const finishedAt = responseQuestion.value.finishedAt;
      if (!createdAt || !finishedAt) return "";
      const differenceInSeconds = Math.floor(
        (new Date(finishedAt).getTime() - new Date(createdAt).getTime()) / 1000
      );
      return formattedDuration(differenceInSeconds);
    });

    const nextQuestion = () => {
      if (
        currentIndex.value <
        (responseQuestion.value?.questions?.length || 0) - 1
      ) {
        currentIndex.value++;
      }
      updateQuery(router, "page", currentIndex.value.toString());
    };

    const previousQuestion = () => {
      if (currentIndex.value > 0) {
        currentIndex.value--;
      }
      updateQuery(router, "page", currentIndex.value.toString());
    };

    const goToQuestion = (index: number) => {
      const total = responseQuestion.value?.questions?.length || 0;
      if (index >= 0 && index < total) {
        currentIndex.value = index;
        visitedQuestions.value.add(index);
      }
    };

    const goBack = () => {
      router.push({
        name: "course_content",
        params: {
          course_id: route.params.course_id,
          content_id: route.params.content_id
        }
      });
    };

    const listResponseQuestions = async () => {
      try {
        await wrapLoading(async () => {
          await store.dispatch("question_module/listResponseQuestions", {
            trainingID: route.params.course_id,
            contentID: route.params.content_id,
            questionnaireID: props.questionnaireId
          });
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    onBeforeMount(async () => {
      await listResponseQuestions();

      const pageFromQuery = Number(route.query.page);
      currentIndex.value =
        !isNaN(pageFromQuery) && pageFromQuery > 0 ? pageFromQuery - 1 : 0;
      visitedQuestions.value.add(currentIndex.value);
    });

    watch(currentIndex, index => {
      visitedQuestions.value.add(index);
      router.replace({ query: { ...route.query, page: String(index + 1) } });
    });

    return {
      responseQuestion,
      currentQuestion,
      currentIndex,
      feedbackMessage,
      timeElapsed,
      goBack,
      goToQuestion,
      nextQuestion,
      previousQuestion,
      isCorrectQuestion,
      isIncorrectQuestion,
      isCorrectionQuestion,
      isUnansweredQuestion,
      isAlternativeSelected,
      isCorrectAlternative,
      isIncorrectAlternative
    };
  }
});
